import { WbTextField } from '@agilelab/plugin-wb-platform';
import { generateComponentIdentifier } from '@agilelab/plugin-wb-platform-common';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { extractCustomProperties, isHidden } from '../../utils';
import { ComponentIdentifierPickerProps } from './schema';
import { useLatest } from 'react-use';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

export const ComponentIdentifierPicker = ({
  schema: {
    title = 'Identifier',
    description = 'Unique identifier of the component',
  },
  uiSchema,
  formContext,
  formData,
  onChange,
  placeholder,
  idSchema,
  required,
  rawErrors,
}: ComponentIdentifierPickerProps) => {
  const onChangeRef = useLatest(onChange);
  const autoFocus = uiSchema['ui:autofocus'];
  const customProps = extractCustomProperties(uiSchema);
  const { debounceMs } = useFormExtraConfigContext();

  // Check if dependency manager is available
  const dependencyManager = formContext?.dependencyManager;
  const rootSchema = formContext?.rootSchema;

  // Extract canonical field path
  // Convert from RJSF's underscore separator to dependency manager's > separator
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [idSchema, rootSchema]);

  // OLD ARCHITECTURE: Auto-compute identifier when dependencies change
  // This will be skipped when dependency manager is enabled
  useEffect(() => {
    if (dependencyManager) return; // Skip if new architecture is enabled

    onChangeRef.current(
      generateComponentIdentifier(
        formContext.name,
        formContext.parentRef ?? formContext.dataproduct,
      ) ?? '',
    );
  }, [
    formContext.dataproduct,
    formContext.name,
    formContext.parentRef,
    onChangeRef,
    dependencyManager,
  ]);

  // NEW ARCHITECTURE: Register dependency handler
  // Re-register when middleware ID changes (middleware is recreated on step switch)
  const middlewareId = dependencyManager?.id;
  useEffect(() => {
    if (!dependencyManager?.isReady) return;

    // Declare which fields this component depends on
    dependencyManager.registerDependencies(fieldPath, [
      'name',
      'dataproduct',
      'parentRef',
    ]);

    // Register handler for this field
    dependencyManager.registerHandler(
      fieldPath,
      (
        _fieldPath: string,
        triggerPath: string,
        triggerValue: any,
        currentFormData: any,
      ) => {
        // Use triggerValue when the trigger is one of our dependencies,
        // otherwise fall back to currentFormData (which reflects the pre-update state)
        const name =
          triggerPath === 'name' ? triggerValue : currentFormData.name;
        const parentRef =
          triggerPath === 'parentRef'
            ? triggerValue
            : currentFormData.parentRef;
        const dataproduct =
          triggerPath === 'dataproduct'
            ? triggerValue
            : currentFormData.dataproduct;

        return (
          generateComponentIdentifier(name, parentRef ?? dataproduct) ?? ''
        );
      },
    );
  }, [dependencyManager, middlewareId, fieldPath]);

  // Handle manual changes
  const handleChange = useCallback(
    (value: string) => {
      // Notify dependency manager to propagate changes to dependents, or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <WbTextField
      id={idSchema?.$id}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      label={title}
      disabled={!(uiSchema['ui:options']?.allowArbitraryValues ?? true)}
      placeholder={placeholder}
      helperText={description}
      required={required}
      value={formData ?? ''}
      debounceMs={debounceMs}
      onChange={({ target: { value } }) => handleChange(value)}
      error={rawErrors?.length > 0 && !formData}
      inputProps={{ autoFocus }}
      {...customProps}
    />
  );
};
